import { debounce, genClassName } from "@disco/disco_core";
import React, { memo, useMemo, useRef } from "react";
import useData from "../../hooks/useData";
import useElementInView from "../../hooks/useElementInView";
import useTrack, { eventTypes } from "../../hooks/useTrack";
import ProductCard from "./ProductCard";

import brandEventPropertyMapper from "../../utils/brandEventPropertyMapper";
import "./brand-row.css";
import BrandRowHeader from "./BrandRowHeader";
import useCachedViewTrack, {
	TRACKED_TARGET,
} from "../../hooks/useCachedViewTrack";
import useImpression from "../../hooks/useImpression";
import {
	SNOWPLOW_EVENT,
	SNOWPLOW_EVENT_DATA_KEY,
} from "../../utils/snowplowEventMigrator";
import {
	ELEMENT_ENGAGEMENT_TARGET,
	ELEMENT_ENGAGEMENT_TYPE,
} from "../../event_conf";

const BrandRow = memo(
	({
		setBrandRef,
		brand,
		upsell = false,
		idx,
		isVisible,
		isParentScrolling,
		...rest
	}) => {
		const track = useTrack();

		const brandRowContainerRef = useRef(null);
		const brandRowBodyRef = useRef(null);

		const { products, id: brandId } = useMemo(() => brand || {}, [brand]);

		const { feedHasUpsell } = useData();

		const defaultEventProps = useMemo(
			() =>
				brandEventPropertyMapper({ brand, idx, upsell, feedHasUpsell }),
			[brand, idx, upsell, feedHasUpsell]
		);
		const { isPartiallyInView } = useElementInView({
			elementRef: brandRowBodyRef,
			active: isVisible,
			debug: idx,
		});

		const isBrandInView = useMemo(
			() => isVisible && isPartiallyInView,
			[isVisible, isPartiallyInView]
		);

		useCachedViewTrack({
			id: brandId,
			key: TRACKED_TARGET.BRAND_ROW,
			event: eventTypes.WIDGET_BRAND_DISPLAY,
			properties: defaultEventProps,
			canTrack: isBrandInView,
		});

		useImpression({
			id: brandId,
			cacheKey: TRACKED_TARGET.VIEWABLE_THRESHOLD_BRAND,
			event: SNOWPLOW_EVENT.WIDGET_VIEWABLE_THRESHOLD_BRAND_DISPLAY,
			properties: defaultEventProps,

			target: brandRowContainerRef.current,
		});

		const handleBrandContainerRef = (el) => {
			setBrandRef(el);
			brandRowContainerRef.current = el;
		};

		const { hasBeenTracked, handleTrack } = useCachedViewTrack({
			id: brandId,
			key: TRACKED_TARGET.BRAND_ROW_SCROLL,
		});

		const instantaneousScrollTrackedRef = useRef(false);
		const handleBrandRowScroll = debounce(() => {
			if (instantaneousScrollTrackedRef.current || hasBeenTracked) return;
			instantaneousScrollTrackedRef.current = true;
			handleTrack();

			track(SNOWPLOW_EVENT.WIDGET_ELEMENT_ENGAGEMENT, {
				...defaultEventProps,
				[SNOWPLOW_EVENT_DATA_KEY]: {
					initiatingTarget: ELEMENT_ENGAGEMENT_TARGET.BRAND_ROW,
					engagementType: ELEMENT_ENGAGEMENT_TYPE.HORIZONTAL_SCROLL,
				},
			});
		}, 100);

		if (!brand) {
			return null;
		}

		return (
			<div
				className="widget-brand-container"
				data-testid="widget-brand-container"
				data-intersectionuid={brandId}
				ref={handleBrandContainerRef}
				{...rest}
			>
				<BrandRowHeader
					brand={brand}
					upsell={upsell}
					defaultEventProps={defaultEventProps}
				/>
				<div
					className={genClassName({
						base: "widget-brand-body",
						conditionals: {
							"widget-brand-body-static": isParentScrolling,
						},
					})}
					data-testid="widget-brand-body"
					ref={brandRowBodyRef}
					onScroll={hasBeenTracked ? undefined : handleBrandRowScroll}
				>
					{Array.isArray(products) &&
						products.map((product, id) => (
							<ProductCard
								key={id}
								idx={id}
								upsell={upsell}
								parentBrand={brand}
								parentBrandRef={brandRowBodyRef.current}
								parentBrandIdx={idx}
								isParentBrandInView={isBrandInView}
								product={product}
							/>
						))}
					<div className="widget-brand-body-spacer" />
				</div>
			</div>
		);
	}
);

export default BrandRow;
